import { useState, useEffect, FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPinOff, Save, Trash2 } from "lucide-react";
import { useToast } from "../components/ToastProvider";
import { useLocationStore } from "../features/locationStore";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  GET_QNAS_BUILDINGS,
  GET_QNAS_STREETS,
  GET_SERVICE_ZONES,
} from "../features/location/locationQueries";
import { CREATE_CUSTOMER_LOCATION } from "../features/location/locationMutations";

interface LocationForm {
  label: string;
  floorNumber: string;
  doorNumber: string;
  buildingNumber: string;
  streetNumber: string;
  zoneNumber: string;
  area: string;
}

interface ServiceZone {
  id: string;
  zone_number: number;
  name: string;
}

interface QnasStreet {
  street_number: number;
  name_en?: string | null;
}

interface QnasBuilding {
  building_number: string;
}

const emptyForm: LocationForm = {
  label: "",
  floorNumber: "",
  doorNumber: "",
  buildingNumber: "",
  streetNumber: "",
  zoneNumber: "",
  area: "",
};

export default function AddEditLocationScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();
  const { addLocation, updateLocation, deleteLocation, getLocation } =
    useLocationStore();

  const isEdit = Boolean(id);
  const [formData, setFormData] = useState<LocationForm>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Zone → street → building, like the QNAS website. Zones are only the ones
  // RUSH collects in; streets and buildings come from the national address list.
  const zoneNo = Number(formData.zoneNumber) || 0;
  const streetNo = Number(formData.streetNumber) || 0;

  const {
    data: zoneData,
    loading: zonesLoading,
    error: zonesError,
  } = useQuery<{ serviceZones: ServiceZone[] }>(GET_SERVICE_ZONES, {
    fetchPolicy: "network-only",
  });
  const {
    data: streetData,
    loading: streetsLoading,
    error: streetsError,
  } = useQuery<{ qnasStreets: QnasStreet[] }>(GET_QNAS_STREETS, {
    variables: { zone: zoneNo },
    skip: !zoneNo,
  });
  const {
    data: buildingData,
    loading: buildingsLoading,
    error: buildingsError,
  } = useQuery<{ qnasBuildings: QnasBuilding[] }>(GET_QNAS_BUILDINGS, {
    variables: { zone: zoneNo, street: streetNo },
    skip: !zoneNo || !streetNo,
  });

  const zones = zoneData?.serviceZones ?? [];
  const streets = zoneNo ? streetData?.qnasStreets ?? [] : [];
  const buildings = zoneNo && streetNo ? buildingData?.qnasBuildings ?? [] : [];
  const noCoverage = !zonesLoading && !zonesError && zones.length === 0;

  const [createCustomerLocation, { loading: savingLocation }] = useMutation(
    CREATE_CUSTOMER_LOCATION,
  );

  useEffect(() => {
    if (zonesError) showToast("Failed to load the areas we serve", "error");
  }, [zonesError, showToast]);

  useEffect(() => {
    if (isEdit && id) {
      const existing = getLocation(id);
      if (existing) {
        setFormData({
          label: existing.label,
          floorNumber: existing.floorNumber ?? "",
          doorNumber: existing.doorNumber ?? "",
          buildingNumber: existing.buildingNumber,
          streetNumber: existing.streetNumber,
          zoneNumber: existing.zoneNumber,
          area: existing.area,
        });
      }
    }
  }, [id, isEdit, getLocation]);

  const clearError = (field: string) => {
    setFieldErrors((prev) => {
      const u = { ...prev };
      delete u[field];
      return u;
    });
  };

  const handleChange = (field: keyof LocationForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };

  // Picking a zone resets the street and building; picking a street resets the building
  const handleZone = (value: string) => {
    const zone = zones.find((z) => String(z.zone_number) === value);
    setFormData((prev) => ({
      ...prev,
      zoneNumber: value,
      area: zone?.name ?? "",
      streetNumber: "",
      buildingNumber: "",
    }));
    clearError("zoneNumber");
  };

  const handleStreet = (value: string) => {
    setFormData((prev) => ({ ...prev, streetNumber: value, buildingNumber: "" }));
    clearError("streetNumber");
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.label.trim()) errors.label = "Required";
    if (!formData.floorNumber.trim()) errors.floorNumber = "Required";
    if (!formData.doorNumber.trim()) errors.doorNumber = "Required";
    if (!formData.buildingNumber.trim()) errors.buildingNumber = "Required";
    if (!formData.streetNumber.trim()) errors.streetNumber = "Required";
    if (!formData.zoneNumber.trim()) errors.zoneNumber = "Required";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const formattedLocation = `${formData.label} - Floor ${formData.floorNumber}, Door ${formData.doorNumber}, Building ${formData.buildingNumber}, Street ${formData.streetNumber}, Zone ${formData.zoneNumber}, ${formData.area}`;

    try {
      if (isEdit && id) {
        updateLocation(id, formData);

        showToast("Location updated successfully", "success");
      } else {
        await createCustomerLocation({
          variables: {
            location: formattedLocation,
          },
        });

        showToast("Location saved successfully", "success");
      }

      navigate(-1);
    } catch (error) {
      // The server explains what's wrong, e.g. a zone we don't collect in
      const message = error instanceof Error ? error.message.replace(/^Error:\s*/i, "") : "";
      showToast(message || "Failed to save location", "error");
    }
  };

  const handleDelete = () => {
    if (id) {
      deleteLocation(id);
      showToast("Location deleted", "success");
      navigate(-1);
    }
  };

  const inputClass = (field: string) =>
    `w-full bg-gray-50 rounded-2xl px-4 py-3 text-gray-700 text-sm outline-none border transition-colors ${
      fieldErrors[field]
        ? "border-red-300 bg-red-50/50"
        : "border-gray-100 focus:border-[#44B6CE]"
    }`;

  return (
    <div className="bg-gray-50 min-h-screen md:min-h-0 md:bg-transparent pb-10 md:max-w-2xl md:mx-auto">
      {/* Header */}
      <div className="bg-white px-5 pt-5 pb-4 shadow-sm rounded-b-[32px] md:rounded-3xl mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 border border-gray-100 rounded-xl flex items-center justify-center bg-white shadow-sm"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-lg font-bold font-poppins text-gray-900">
                {isEdit ? "Edit Location" : "Add New Location"}
              </h1>
              <p className="text-[11px] text-gray-400">
                Save address for quick checkout
              </p>
            </div>
          </div>
          {isEdit && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center"
            >
              <Trash2 size={16} className="text-red-500" />
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="px-5 md:px-0 space-y-4">
        {/* Label Group */}
        <div className="bg-white rounded-[28px] p-5 shadow-sm border border-gray-100">
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
            Address Label
          </label>
          <input
            type="text"
            placeholder="e.g. Home, Office"
            value={formData.label}
            onChange={(e) => handleChange("label", e.target.value)}
            className={inputClass("label")}
          />
        </div>

        {/* Details Group */}
        <div className="bg-white rounded-[28px] p-5 shadow-sm border border-gray-100 space-y-4">
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">
            Location Details
          </label>

          {noCoverage ? (
            <div className="flex gap-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3">
              <MapPinOff size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                We aren&apos;t collecting from homes yet. You can still use a locker for your orders.
              </p>
            </div>
          ) : (
            <>
              {/* Zone → Street → Building, from the national address list */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 mb-1 ml-1">
                  Zone
                </label>
                <select
                  value={formData.zoneNumber}
                  onChange={(e) => handleZone(e.target.value)}
                  disabled={zonesLoading}
                  className={inputClass("zoneNumber")}
                >
                  <option value="" disabled>
                    {zonesLoading ? "Loading zones..." : "Select your zone"}
                  </option>
                  {zones.map((z) => (
                    <option key={z.id} value={String(z.zone_number)}>
                      Zone {z.zone_number} · {z.name}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-400 mt-1 ml-1">
                  Only the areas we collect from are listed. Your zone number is on the blue plate on your building.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1 ml-1">
                    Street
                  </label>
                  <select
                    value={formData.streetNumber}
                    onChange={(e) => handleStreet(e.target.value)}
                    disabled={!zoneNo || streetsLoading}
                    className={inputClass("streetNumber")}
                  >
                    <option value="" disabled>
                      {!zoneNo ? "Choose zone first" : streetsLoading ? "Loading..." : "Select street"}
                    </option>
                    {streets.map((st) => (
                      <option key={st.street_number} value={String(st.street_number)}>
                        {st.street_number}
                        {st.name_en ? ` · ${st.name_en}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1 ml-1">
                    Building
                  </label>
                  <select
                    value={formData.buildingNumber}
                    onChange={(e) => handleChange("buildingNumber", e.target.value)}
                    disabled={!streetNo || buildingsLoading}
                    className={inputClass("buildingNumber")}
                  >
                    <option value="" disabled>
                      {!streetNo ? "Choose street first" : buildingsLoading ? "Loading..." : "Select building"}
                    </option>
                    {buildings.map((b) => (
                      <option key={b.building_number} value={b.building_number}>
                        {b.building_number}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {(streetsError || buildingsError) && (
                <p className="text-xs text-red-500 ml-1">
                  Couldn&apos;t load the address list right now. Please try again in a moment.
                </p>
              )}

              {/* Floor & Door */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1 ml-1">
                    Floor No.
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 3"
                    value={formData.floorNumber}
                    onChange={(e) => handleChange("floorNumber", e.target.value)}
                    className={inputClass("floorNumber")}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1 ml-1">
                    Door No.
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 12"
                    value={formData.doorNumber}
                    onChange={(e) => handleChange("doorNumber", e.target.value)}
                    className={inputClass("doorNumber")}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <button
          type="submit"
          disabled={savingLocation || noCoverage}
          className={`w-full h-14 bg-[#44B6CE] text-white rounded-2xl flex items-center justify-center gap-2 font-bold shadow-lg shadow-cyan-100 active:scale-[0.98] transition-all ${
            savingLocation ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          <Save size={18} />
          {savingLocation
            ? "Saving..."
            : isEdit
              ? "Update Location"
              : "Save Location"}
        </button>
      </form>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] p-8 w-full max-w-sm shadow-xl">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Delete Address?
            </h3>
            <p className="text-sm text-gray-500 mb-8 leading-relaxed">
              This will remove this location from your saved list permanently.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 h-12 rounded-2xl border border-gray-200 text-gray-600 font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 h-12 rounded-2xl bg-red-500 text-white font-bold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
